import { getManySeasonDataFromJson, loadJsonData } from "../../utils/hybridDataLoader";

// Mirrors the backend /smart-fantasy/race-data query: the NextGen era only
const START_YEAR = 2022;
const COUNTED_STATUSES = ["finished", "crash", "failure"]; // excludes DNS/DNQ

const byDriverThenNewest = (a, b) =>
  a.driver_name.localeCompare(b.driver_name) || new Date(b.race_date) - new Date(a.race_date);

/**
 * Build the smart fantasy payload from season race rows, the same way the backend does.
 * A driver's group is the one on their latest race of `season`.
 */
export const buildSmartFantasyData = ({
  races,
  entryList,
  trackSimilarity,
  groupType,
  groups,
  track,
  season
}) => {
  const groupKey = groupType === "open" ? "open_group" : "star_group";
  const wantedGroups = groups.includes("All") ? null : new Set(groups);
  const entered = new Set(entryList[String(season)] || []);

  const latestGroupByDriver = new Map();
  races
    .filter(r => r.season_year === season && r[groupKey])
    .sort((a, b) => new Date(a.race_date) - new Date(b.race_date))
    .forEach(r => latestGroupByDriver.set(r.driver_name, r[groupKey]));

  const groupDriverSet = new Set(
    [...latestGroupByDriver]
      .filter(([name, group]) => entered.has(name) && (!wantedGroups || wantedGroups.has(group)))
      .map(([name]) => name)
  );
  if (groupDriverSet.size === 0) {
    return { groupDrivers: [] };
  }

  const similarTracks = new Set(trackSimilarity[track] || []);
  const driverRaces = races
    .filter(r =>
      groupDriverSet.has(r.driver_name) &&
      r.season_year >= START_YEAR &&
      COUNTED_STATUSES.includes(r.status)
    )
    .sort(byDriverThenNewest);

  const result = {
    trackRaces: [],
    similarTrackRaces: [],
    allRaces: [],
    currentSeasonRaces: [],
    pastSeasonRaces: [],
    last5Races: [],
    last10Races: [],
    groupDrivers: [...new Set(driverRaces.map(r => r.driver_name))]
  };

  let rank = 0;
  let rankedDriver = null;
  driverRaces.forEach(row => {
    rank = row.driver_name === rankedDriver ? rank + 1 : 1;
    rankedDriver = row.driver_name;

    const race = { ...row, car_number: Number(row.car_number), track_short_name: row.race_name || null };

    result.allRaces.push(race);
    (race.season_year === season ? result.currentSeasonRaces : result.pastSeasonRaces).push(race);
    if (race.track_name === track) {
      result.trackRaces.push(race);
    } else if (similarTracks.has(race.track_name)) {
      result.similarTrackRaces.push(race);
    }
    if (rank <= 5) result.last5Races.push(race);
    if (rank <= 10) result.last10Races.push(race);
  });

  // The current track is also part of the similar-tracks view
  result.similarTrackRaces.push(...result.trackRaces);

  return {
    ...result,
    metadata: {
      drivers_count: result.groupDrivers.length,
      total_races: result.allRaces.length,
      track_specific_races: result.trackRaces.length,
      similar_track_races: result.similarTrackRaces.length,
      current_season_races: result.currentSeasonRaces.length,
      start_year: START_YEAR,
      target_season: season,
      target_track: track
    },
    source: "json_fallback"
  };
};

/**
 * Same payload as the API, built in the browser from the season JSON files
 */
export const loadSmartFantasyDataFromJson = async ({ groupType, groups, track, season }) => {
  const [races, entryList, trackSimilarity] = await Promise.all([
    getManySeasonDataFromJson("race", START_YEAR),
    loadJsonData("entry_list.json"),
    loadJsonData("track_similarity.json")
  ]);
  return buildSmartFantasyData({ races, entryList, trackSimilarity, groupType, groups, track, season });
};
